/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
    AlertDialog,
    AlertDialogContent,
    Badge,
    Button,
    Card,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    MoreButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";
import CheckPrimaryColorIcon from "../../../../components/Icons/CheckPrimaryColor";
import { useDebounce } from "../../../../hooks/helpers";
import { useDoesUserHavePermission } from "../../../../hooks/permissions";
import useToast from "../../../../hooks/useToast";
import {
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import { UserPermisions } from "../../../../utils/types/user";
import { useFetchProviders } from "../../../providers/hooks/providers.queries";
import { useFetchNoteRuleBypasses } from "../../hooks/note-rule-bypass.queries";
import { useUpdateNoteRuleBypassStatus } from "../../hooks/note-rule-bypass.mutations";
import {
    INoteRuleBypassListRow,
    NoteRule,
    NoteRuleBypassStatus,
    RULE_LABELS,
} from "../../types/note-rule-bypass.types";
import AddNoteRuleBypassForm from "../AddNoteRuleBypassForm/AddNoteRuleBypassForm";
import ChevronDownIcon from "../../../../icons/ChevronDown";

const SORTABLE_COLUMNS = ["provider_name", "client_name", "created_at"];

type DialogKind =
    | { kind: "add"; rule: NoteRule }
    | {
          kind: "status";
          bypass: INoteRuleBypassListRow;
          next: NoteRuleBypassStatus;
      }
    | null;

export default function NoteRuleBypassList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogInView, setDialogInView] = React.useState<DialogKind>(null);
    const [showProviderPopover, setShowProviderPopover] = React.useState(false);
    const [providerSearchString, setProviderSearchString] = React.useState("");
    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({ key: "", direction: "" });

    const canWriteNoteRuleBypass = useDoesUserHavePermission([
        UserPermisions.NOTE_RULE_BYPASS_WRITE,
    ]);

    const queryClient = useQueryClient();
    const { toast } = useToast();
    const updateStatus = useUpdateNoteRuleBypassStatus();
    const createIsMutating = useIsMutating(["create-note-rule-bypass"]);

    const pageFilter = Number(searchParams.get("page") || 1);
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const ruleFilter = (searchParams.get("rule") as NoteRule) || undefined;
    const statusFilter =
        (searchParams.get("status") as NoteRuleBypassStatus) || undefined;
    const providerNameFilter = searchParams.get("provider_name") || "";
    const clientNameFilter = searchParams.get("client_name") || "";
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");

    const debouncedProviderSearch = useDebounce(providerSearchString);
    const debouncedClientName = useDebounce(clientNameFilter);

    const providersQuery = useFetchProviders({
        page: 1,
        limit: 20,
        search_string: debouncedProviderSearch,
    });

    const fromDateValue = convertDateFilterStringToDate(fromDateFilter);
    const toDateValue = convertDateFilterStringToDate(toDateFilter);

    const sortBy = SORTABLE_COLUMNS.includes(sortConfig.key)
        ? sortConfig.key
        : undefined;
    const sortDirection = sortConfig.direction || undefined;

    const { data, isLoading, isSuccess, error } = useFetchNoteRuleBypasses({
        rule: ruleFilter,
        status: statusFilter,
        provider_name: providerNameFilter || undefined,
        client_name: debouncedClientName || undefined,
        from_date: fromDateFilter,
        to_date: toDateFilter,
        sort_by: sortBy,
        sort_direction: sortDirection,
        page: pageFilter,
        limit: limitFilter,
    });

    const isDisabling =
        dialogInView?.kind === "status" &&
        dialogInView.next === NoteRuleBypassStatus.DISABLED;

    const setParam = (key: string, value: string | null) => {
        if (!value) searchParams.delete(key);
        else searchParams.set(key, value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    const handleStatusChange = () => {
        if (dialogInView?.kind !== "status") return;
        const { bypass, next } = dialogInView;
        updateStatus.mutate(
            { bypass_id: bypass.bypass_id, status: next },
            {
                onSuccess: (res) => {
                    toast({
                        mode: "success",
                        message:
                            res.message ||
                            (next === NoteRuleBypassStatus.ACTIVE
                                ? "Note rule override reactivated"
                                : "Note rule override disabled"),
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["note-rule-bypasses"],
                    });
                    setDialogInView(null);
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update note rule override status",
                    });
                },
            }
        );
    };

    const columns: ColumnDef<INoteRuleBypassListRow>[] = [
        {
            accessorKey: "provider_name",
            header: "PROVIDER",
            cell: ({ row }) =>
                displayNameInRightFormat(row.original.provider_name),
        },
        {
            accessorKey: "client_name",
            header: "CLIENT",
            cell: ({ row }) =>
                displayNameInRightFormat(row.original.client_name),
        },
        {
            accessorKey: "rule",
            header: "RULE",
            cell: ({ row }) => RULE_LABELS[row.original.rule],
        },
        {
            accessorKey: "status",
            header: "STATUS",
            cell: ({ row }) => (
                <Badge
                    content={makeStringFirstLetterCapital(
                        removeEnumUnderscore(row.original.status)
                    )}
                    type={
                        row.original.status === NoteRuleBypassStatus.ACTIVE
                            ? "success"
                            : "danger"
                    }
                />
            ),
        },
        {
            accessorKey: "created_by",
            header: "CREATED BY",
            cell: ({ row }) =>
                row.original.created_by.first_name
                    ? displayNameInRightFormat({
                          firstName: row.original.created_by.first_name,
                          lastName: row.original.created_by.last_name,
                      })
                    : "--",
        },
        {
            accessorKey: "created_at",
            header: "CREATED AT",
            cell: ({ row }) =>
                row.original.created_at
                    ? formatDate(
                          row.original.created_at,
                          false,
                          "MM/dd/yyyy, hh:mmaaa"
                      )
                    : "--",
        },
        {
            id: "actions",
            header: "",
            cell: ({ row }) =>
                canWriteNoteRuleBypass ? (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <MoreButton />
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {row.original.status ===
                            NoteRuleBypassStatus.ACTIVE ? (
                                <DropdownItem
                                    onClick={() =>
                                        setDialogInView({
                                            kind: "status",
                                            bypass: row.original,
                                            next: NoteRuleBypassStatus.DISABLED,
                                        })
                                    }
                                >
                                    Disable
                                </DropdownItem>
                            ) : (
                                <DropdownItem
                                    onClick={() =>
                                        setDialogInView({
                                            kind: "status",
                                            bypass: row.original,
                                            next: NoteRuleBypassStatus.ACTIVE,
                                        })
                                    }
                                >
                                    Reactivate
                                </DropdownItem>
                            )}
                        </DropdownContent>
                    </Dropdown>
                ) : null,
        },
    ];

    return (
        <>
            <AlertDialog open={dialogInView?.kind === "status"}>
                <AlertDialogContent
                    isDeleting={isDisabling}
                    onCancelClick={() => setDialogInView(null)}
                    title={
                        isDisabling ? "Disable override" : "Reactivate override"
                    }
                    description={
                        isDisabling
                            ? "Are you sure you want to disable this note rule override?"
                            : "Are you sure you want to reactivate this note rule override?"
                    }
                    saveText={isDisabling ? "Disable" : "Reactivate"}
                    cancelText="Cancel"
                    isCancelBtnDisabled={updateStatus.isLoading}
                    isSaveBtnDisabled={updateStatus.isLoading}
                    onSaveClick={handleStatusChange}
                />
            </AlertDialog>

            {dialogInView?.kind === "add" && (
                <Dialog open>
                    <DialogContent
                        variant="center"
                        handleCloseDialog={() => setDialogInView(null)}
                        title={`Add override: ${
                            RULE_LABELS[dialogInView.rule]
                        }`}
                        showFooter
                        saveText="Add Override"
                        cancelText="Cancel"
                        submitBtnFormValue="add-note-rule-bypass"
                        isCancelBtnDisabled={createIsMutating > 0}
                        isSubmitBtnDisabled={createIsMutating > 0}
                    >
                        <AddNoteRuleBypassForm
                            rule={dialogInView.rule}
                            onFormSubmit={() => setDialogInView(null)}
                        />
                    </DialogContent>
                </Dialog>
            )}

            <Card classNames="mb-[--spacing-header]">
                <div className="flex flex-col gap-[--spacing-items]">
                    <SearchInput
                        defaultValue={clientNameFilter}
                        onChange={(e) =>
                            setParam("client_name", e.target.value)
                        }
                        containerClass="w-[400px]"
                        placeholder="Search by client name"
                    />
                    <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                        <div className="flex items-center flex-wrap gap-[--spacing-items]">
                            <Popover
                                open={showProviderPopover}
                                onOpenChange={(open) => {
                                    setShowProviderPopover(open);
                                    setProviderSearchString("");
                                }}
                            >
                                <PopoverTrigger
                                    asChild
                                    onClick={() => setShowProviderPopover(true)}
                                >
                                    <FilterButton
                                        className={
                                            providerNameFilter
                                                ? "capitalize"
                                                : "normal-case"
                                        }
                                    >
                                        {providerNameFilter
                                            ? truncateString(providerNameFilter)
                                            : "Provider"}
                                    </FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="start">
                                    <SearchInput
                                        placeholder="Search by name"
                                        onChange={(e) =>
                                            setProviderSearchString(
                                                e.target.value
                                            )
                                        }
                                    />
                                    <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                        {providersQuery.isLoading ? (
                                            <Loader wrapperClassName="h-[150px]" />
                                        ) : null}
                                        {providersQuery.error && (
                                            <ListState
                                                isError
                                                context="general"
                                                stateHelperText="Please try again later"
                                                errorMsg={
                                                    providersQuery.error
                                                        ?.response?.data
                                                        ?.message ||
                                                    "Error loading providers"
                                                }
                                                cardClass="h-[150px]"
                                            />
                                        )}
                                        {providersQuery.data?.data &&
                                            providersQuery.isSuccess && (
                                                <>
                                                    <Button
                                                        size="auto"
                                                        variant="can-hover"
                                                        className="font-medium capitalize w-full justify-start shrink-0 mt-2"
                                                        onClick={() => {
                                                            setParam(
                                                                "provider_name",
                                                                null
                                                            );
                                                            setShowProviderPopover(
                                                                false
                                                            );
                                                            setProviderSearchString(
                                                                ""
                                                            );
                                                        }}
                                                    >
                                                        All
                                                    </Button>
                                                    {providersQuery.data.data.map(
                                                        (provider) => (
                                                            <Button
                                                                key={
                                                                    provider.provider_id
                                                                }
                                                                size="auto"
                                                                variant="can-hover"
                                                                className="font-medium capitalize w-full justify-start shrink-0"
                                                                onClick={() => {
                                                                    setParam(
                                                                        "provider_name",
                                                                        provider.provider_name
                                                                    );
                                                                    setShowProviderPopover(
                                                                        false
                                                                    );
                                                                    setProviderSearchString(
                                                                        ""
                                                                    );
                                                                }}
                                                            >
                                                                {displayNameInRightFormat(
                                                                    provider.provider_name
                                                                )}
                                                                {providerNameFilter ===
                                                                provider.provider_name ? (
                                                                    <CheckPrimaryColorIcon />
                                                                ) : null}
                                                            </Button>
                                                        )
                                                    )}
                                                </>
                                            )}
                                    </div>
                                </PopoverContent>
                            </Popover>

                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton
                                        className={
                                            ruleFilter
                                                ? "capitalize"
                                                : "normal-case"
                                        }
                                    >
                                        {ruleFilter
                                            ? RULE_LABELS[ruleFilter]
                                            : "Rule"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width={180}>
                                    <DropdownItem
                                        isSelected={!ruleFilter}
                                        onClick={() => setParam("rule", null)}
                                    >
                                        All
                                    </DropdownItem>
                                    {Object.values(NoteRule).map((rule) => (
                                        <DropdownItem
                                            key={rule}
                                            isSelected={ruleFilter === rule}
                                            onClick={() =>
                                                setParam("rule", rule)
                                            }
                                        >
                                            {RULE_LABELS[rule]}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>

                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton
                                        className={
                                            statusFilter
                                                ? "capitalize"
                                                : "normal-case"
                                        }
                                    >
                                        {statusFilter
                                            ? makeStringFirstLetterCapital(
                                                  statusFilter
                                              )
                                            : "Status"}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent width={180}>
                                    <DropdownItem
                                        isSelected={!statusFilter}
                                        onClick={() => setParam("status", null)}
                                    >
                                        All
                                    </DropdownItem>
                                    {Object.values(NoteRuleBypassStatus).map(
                                        (status) => (
                                            <DropdownItem
                                                key={status}
                                                isSelected={
                                                    statusFilter === status
                                                }
                                                onClick={() =>
                                                    setParam("status", status)
                                                }
                                            >
                                                {makeStringFirstLetterCapital(
                                                    status
                                                )}
                                            </DropdownItem>
                                        )
                                    )}
                                </DropdownContent>
                            </Dropdown>

                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>Date Created</FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="end">
                                    <div className="mb-1">
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            selected={fromDateValue}
                                            onChange={(date: Date | null) =>
                                                setParam(
                                                    "from_date",
                                                    date
                                                        ? format(
                                                              date,
                                                              "yyyy-MM-dd"
                                                          )
                                                        : null
                                                )
                                            }
                                            maxDate={
                                                toDateValue ||
                                                new Date(Date.now())
                                            }
                                            isClearable
                                        />
                                    </div>
                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={toDateValue}
                                        onChange={(date: Date | null) =>
                                            setParam(
                                                "to_date",
                                                date
                                                    ? format(date, "yyyy-MM-dd")
                                                    : null
                                            )
                                        }
                                        minDate={fromDateValue}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                        {canWriteNoteRuleBypass && (
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <Button iconRight={<ChevronDownIcon />}>
                                        Add Bypass
                                    </Button>
                                </DropdownTrigger>
                                <DropdownContent align="end">
                                    {Object.values(NoteRule).map((rule) => (
                                        <DropdownItem
                                            key={rule}
                                            onClick={() =>
                                                setDialogInView({
                                                    kind: "add",
                                                    rule,
                                                })
                                            }
                                        >
                                            {RULE_LABELS[rule]}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>
                        )}
                    </div>
                </div>
            </Card>

            {isLoading ? <Loader /> : null}

            {error && error?.response?.status !== 404 && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        "Cannot display note rule overrides at this time. Please try again later"
                    }
                />
            )}

            {data && isSuccess && data.data.length === 0 && (
                <ListState
                    isError={false}
                    stateHelperText="Note rule overrides will appear here once added"
                    emptyMessage="No note rule overrides added yet"
                />
            )}

            {data && isSuccess && data.data.length > 0 && (
                <Table
                    columns={columns}
                    data={data.data}
                    setSortConfig={setSortConfig}
                    sortConfig={sortConfig}
                    hasPagination={data.total_count > 20}
                    pagination={
                        <Pagination
                            totalCount={data.total_count}
                            totalPages={data.total_pages}
                            currentPage={pageFilter}
                            onPageChange={handlePageChange}
                            limit={limitFilter}
                            onLimitChange={handleLimitChange}
                        />
                    }
                />
            )}
        </>
    );
}
